// Receiving — owner view of purchase orders and their line-by-line receipt confirmations. The owner
// can set shipping details (PO/expected delivery) and also confirm receipts (same form the canner uses).
import { useEffect, useRef, useState } from "react";
import type { Order, OrderLine } from "@shared/types.ts";
import { useData } from "../state/store.tsx";
import { lineArrival, trackingUrl } from "@shared/engine.ts";
import { Badge, EmptyState, Modal, LabeledInput } from "../components/ui.tsx";
import { ReceiveForm } from "../components/ReceiveForm.tsx";
import { fmtDate, mass, money, todayISO } from "../lib/format.ts";

const arrivalBadge = (state: string) =>
  state === "received" ? <Badge tone="ok">Received</Badge>
    : state === "arrived" ? <Badge tone="danger">Arrived — confirm</Badge>
    : state === "in_transit" ? <Badge tone="brand">In transit</Badge>
    : <Badge tone="muted">Not shipped</Badge>;

const orderTone = (s: Order["status"]) => (s === "Received" ? "ok" : s === "Partially Received" ? "brand" : s === "Ordered" ? "warn" : "muted");

export default function Receiving({ focusRunId }: { focusRunId?: string | null }) {
  const { data, mutate } = useData();
  const [open, setOpen] = useState<{ order: Order; line: OrderLine } | null>(null);
  const [edit, setEdit] = useState<{ order: Order; line: OrderLine } | null>(null);
  // When opened from a notification, scroll to + ring the order for that run.
  const focusRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (focusRunId && focusRef.current) focusRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [focusRunId]);
  if (!data) return null;
  const cur = data.business.currency;
  const massUnit = data.business.massUnit || "lb";
  const orders = data.orders;
  const deleteOrder = (o: Order) => {
    if (!confirm(`Delete this order (${o.lines.filter((l) => l.toOrderLbs > 0).length} lines)? This can't be undone.`)) return;
    void mutate((draft) => {
      draft.orders = draft.orders.filter((x) => x.id !== o.id);
      if (o.runId) { const run = draft.runs.find((r) => r.id === o.runId); if (run) { run.orderId = undefined; if (run.status === "Ingredients Ordered") run.status = "Booked"; } }
    });
  };
  // Link (or unlink) an order to a production run — so its shipment/receipt dates flow to that run's
  // timeline. Keeps both sides in sync (order.runId ↔ run.orderId).
  const linkRun = (o: Order, runId: string) => {
    void mutate((draft) => {
      const ord = draft.orders.find((x) => x.id === o.id);
      if (!ord) return;
      if (ord.runId) { const prev = draft.runs.find((r) => r.id === ord.runId); if (prev && prev.orderId === ord.id) prev.orderId = undefined; }
      ord.runId = runId || undefined;
      if (runId) { const run = draft.runs.find((r) => r.id === runId); if (run) run.orderId = ord.id; }
    });
  };
  const activeRuns = data.runs.filter((r) => r.status !== "Cancelled").sort((a, b) => (a.scheduledDate < b.scheduledDate ? 1 : -1));

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-sm font-semibold">Receiving</h2>
        <p className="text-muted text-xs mt-0.5">Purchase orders and canner confirmations (lot, expiry, quantities, photo evidence).</p>
      </div>

      {orders.length === 0 ? (
        <div className="card"><EmptyState title="No orders yet" sub="Generate an order from Ingredient Ordering to start tracking receipts." /></div>
      ) : (
        orders.map((o) => {
          const lines = o.lines.filter((l) => l.toOrderLbs > 0);
          const received = lines.filter((l) => l.received?.confirmedAt).length;
          const focus = !!focusRunId && o.runId === focusRunId;
          return (
            <div key={o.id} ref={focus ? focusRef : undefined} className={`card overflow-x-auto ${focus ? "ring-2 ring-brand ring-offset-2" : ""}`}>
              <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                <div>
                  <div className="text-sm font-semibold">Order · {fmtDate(o.createdAt)}</div>
                  <div className="text-xs text-muted flex items-center gap-1.5 mt-0.5">
                    <span>For run</span>
                    <select className="input w-auto py-0.5 text-xs" value={o.runId || ""} onChange={(e) => linkRun(o, e.target.value)}>
                      <option value="">Ad-hoc (no run)</option>
                      {activeRuns.map((r) => <option key={r.id} value={r.id}>{fmtDate(r.scheduledDate)}</option>)}
                    </select>
                    <span>· {received}/{lines.length} received</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge tone={orderTone(o.status)}>{o.status}</Badge>
                  <button className="btn-sm text-danger hover:bg-danger/5" onClick={() => deleteOrder(o)}>Delete</button>
                </div>
              </div>
              <table className="w-full min-w-[720px]">
                <thead><tr>
                  <th className="th">Ingredient</th>
                  <th className="th">Supplier</th>
                  <th className="th text-right">To order</th>
                  <th className="th">PO #</th>
                  <th className="th">Expected</th>
                  <th className="th">Tracking</th>
                  <th className="th">Received</th>
                  <th className="th text-right"></th>
                </tr></thead>
                <tbody>
                  {lines.map((l) => {
                    const state = lineArrival(l, todayISO());
                    const turl = trackingUrl(l.carrier, l.trackingNumber);
                    return (
                    <tr key={l.id}>
                      <td className="td font-medium">{l.ingredient}</td>
                      <td className="td text-muted">{l.supplier || "—"}</td>
                      <td className="td text-right tabular-nums">{mass(l.toOrderLbs, massUnit)}</td>
                      <td className="td text-muted">{l.poNumber || "—"}</td>
                      <td className="td text-muted">{l.expectedDelivery ? fmtDate(l.expectedDelivery) : "—"}</td>
                      <td className="td">
                        {arrivalBadge(state)}
                        {turl && <a href={turl} target="_blank" rel="noreferrer" className="block text-[11px] text-brand hover:underline">{l.carrier || "track"} {l.trackingNumber}</a>}
                      </td>
                      <td className="td">
                        {l.received?.confirmedAt ? (
                          <span className="inline-flex items-center gap-1.5">
                            <Badge tone="ok">✓ {fmtDate(l.received.dateReceived)}</Badge>
                            {l.received.photos.length > 0 && <span className="text-[11px] text-muted">{l.received.photos.length}📷</span>}
                          </span>
                        ) : <Badge tone="warn">Pending</Badge>}
                      </td>
                      <td className="td text-right">
                        <div className="inline-flex gap-1.5">
                          <button className="btn-sm border border-edge hover:bg-paper" onClick={() => setEdit({ order: o, line: l })}>Edit</button>
                          <button className="btn-sm border border-edge hover:bg-paper" onClick={() => setOpen({ order: o, line: l })}>
                            {l.received?.confirmedAt ? "Receipt" : "Confirm"}
                          </button>
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
                <tfoot><tr>
                  <td className="td font-semibold" colSpan={7}>Order total</td>
                  <td className="td text-right font-semibold tabular-nums">{money(o.lines.reduce((a, l) => a + l.lineCost, 0), cur)}</td>
                </tr></tfoot>
              </table>
            </div>
          );
        })
      )}

      {edit && <ShippingModal order={edit.order} line={edit.line} onClose={() => setEdit(null)} />}
      {open && <LineModal order={open.order} line={open.line} onClose={() => setOpen(null)} />}
    </div>
  );
}

// Owner-side shipping editor — PO, expected delivery, and shipment tracking. (The manufacturer
// confirms the physical receipt separately, in the Confirm/Receipt modal.)
function ShippingModal({ order, line, onClose }: { order: Order; line: OrderLine; onClose: () => void }) {
  const { data, mutate, saving } = useData();
  const [poNumber, setPo] = useState(line.poNumber || "");
  const [expectedDelivery, setExp] = useState(line.expectedDelivery || "");
  const [qtyOrdered, setQty] = useState(line.qtyOrdered != null ? String(line.qtyOrdered) : "");
  const [uom, setUom] = useState(line.uom || ((data?.business.massUnit || "lb") === "kg" ? "Kilograms" : "Pounds"));
  const [carrier, setCarrier] = useState(line.carrier || "");
  const [trackingNumber, setTracking] = useState(line.trackingNumber || "");
  const [shippedDate, setShipped] = useState(line.shippedDate || "");
  const [markedArrived, setArrived] = useState(!!line.markedArrived);
  const save = async () => {
    await mutate((d) => {
      const l = d.orders.find((x) => x.id === order.id)?.lines.find((x) => x.id === line.id);
      if (l) {
        l.poNumber = poNumber || undefined;
        l.expectedDelivery = expectedDelivery || undefined;
        l.qtyOrdered = qtyOrdered === "" ? undefined : Number(qtyOrdered);
        l.uom = uom || undefined;
        l.carrier = carrier || undefined;
        l.trackingNumber = trackingNumber || undefined;
        l.shippedDate = shippedDate || undefined;
        l.markedArrived = markedArrived || undefined;
      }
    });
    onClose();
  };
  return (
    <Modal title={`Shipping details · ${line.ingredient}`} onClose={onClose}>
      <div className="space-y-3">
        <p className="text-xs text-muted">Your side of the order — PO, expected delivery and shipment tracking. The manufacturer confirms the physical receipt separately.</p>
        <div className="grid grid-cols-2 gap-3">
          <LabeledInput label="PO / transfer #"><input className="input" value={poNumber} onChange={(e) => setPo(e.target.value)} /></LabeledInput>
          <LabeledInput label="Expected delivery"><input type="date" className="input" value={expectedDelivery} onChange={(e) => setExp(e.target.value)} /></LabeledInput>
          <LabeledInput label="Qty ordered"><input type="number" step="any" className="input" value={qtyOrdered} onChange={(e) => setQty(e.target.value)} /></LabeledInput>
          <LabeledInput label="UoM"><input className="input" value={uom} onChange={(e) => setUom(e.target.value)} /></LabeledInput>
          <LabeledInput label="Carrier" hint="UPS / FedEx / USPS / DHL / Royal Mail"><input className="input" value={carrier} onChange={(e) => setCarrier(e.target.value)} /></LabeledInput>
          <LabeledInput label="Tracking #"><input className="input" value={trackingNumber} onChange={(e) => setTracking(e.target.value)} /></LabeledInput>
          <LabeledInput label="Shipped date"><input type="date" className="input" value={shippedDate} onChange={(e) => setShipped(e.target.value)} /></LabeledInput>
          <label className="flex items-center gap-2 text-sm mt-6"><input type="checkbox" checked={markedArrived} onChange={(e) => setArrived(e.target.checked)} /> Mark arrived</label>
        </div>
        {trackingUrl(carrier, trackingNumber) && <a href={trackingUrl(carrier, trackingNumber)!} target="_blank" rel="noreferrer" className="text-xs text-brand hover:underline">Open tracking ↗</a>}
        <div className="flex justify-end gap-2 pt-1">
          <button className="btn-ghost btn-sm" onClick={onClose}>Cancel</button>
          <button className="btn-primary btn-sm" disabled={saving} onClick={save}>{saving ? "…" : "Save"}</button>
        </div>
      </div>
    </Modal>
  );
}

function LineModal({ order, line, onClose }: { order: Order; line: OrderLine; onClose: () => void }) {
  const { data, reload } = useData();
  return (
    <Modal title={`Confirm receipt · ${line.ingredient}`} onClose={onClose} wide>
      <div className="space-y-3">
        <p className="text-xs text-muted">Record what physically landed — lot, expiry, quantities and photo evidence. To edit the PO, expected delivery or tracking, use <span className="font-medium">Edit</span> instead.</p>
        <ReceiveForm businessId={data!.business.id} orderId={order.id} line={line} massUnit={data!.business.massUnit || "lb"} onDone={() => { void reload(); onClose(); }} />
      </div>
    </Modal>
  );
}
